<script setup lang="ts">
// HL phone icon (Paolo 2026-11-04): the state of the wireless-debugging phone that sells in the HL app for the user in
// context, at the right end of the always-visible status bar. Four states, drawn as inline SVGs in the shapes of the
// emoji Paolo named (emoji glyphs cannot be recoloured, so they are drawn):
//   unreachable  📴 U+1F4F4  red   — wireless debugging off / off the home wifi: nothing reaches the phone
//   needs_login  📲 U+1F4F2  blue, red arrow — the phone is reachable but HL is not logged in: go and log in
//   ready        📱 U+1F4F1  blue  — armed: HL logged in over wireless debugging
//   busy         📳 U+1F4F3  blue, vibrating — the sell driver is on the phone right now (orders being placed)
// Hidden when the server says the user is not served by the phone (everyone but the phone user and the users linked
// under them), and until the first answer arrives. Polls GET /api/hl-app/phone/presence at the cadence the server
// answers in refreshSeconds (2026-11-05: 5 s inside 07:00-09:00 UK so the icon turns within 20 s, 30 s otherwise;
// intervalMs until the first answer) while the tab is visible, and at once when it becomes visible again.
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { api, type PhonePresence, type PhonePresenceState } from '../api/client'

const props = withDefaults(defineProps<{ intervalMs?: number }>(), { intervalMs: 30_000 })

const presence = ref<PhonePresence | null>(null)
const served = computed(() => presence.value?.served === true && presence.value.state !== null)
const state = computed<PhonePresenceState>(() => presence.value?.state ?? 'unreachable')

const TITLES: Record<PhonePresenceState, string> = {
  unreachable: 'HL phone unreachable — wireless debugging is off or the phone is off the home wifi',
  needs_login: 'HL phone reachable but HL is not logged in — open the HL app on the phone and log in',
  ready: 'HL phone armed — HL logged in, ready to sell in the app',
  busy: 'HL phone busy — placing orders in the HL app',
}
const title = computed(() => TITLES[state.value])

let timer: ReturnType<typeof setTimeout> | null = null
let stopped = false

function delayMs(): number {
  const s = presence.value?.refreshSeconds
  return typeof s === 'number' && s > 0 ? s * 1000 : props.intervalMs
}

function schedule() {
  if (stopped) return
  timer = setTimeout(async () => { await poll(); schedule() }, delayMs())
}

async function poll() {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
  try {
    presence.value = await api.phonePresence()
  } catch (e) {
    // 401/403 = the server told us this user may not ask: hide. Anything else (network blip, 5xx, a test double
    // without the method) keeps the last known state — the API pill already reports an outage.
    const status = (e as { status?: number } | null)?.status
    if (status === 401 || status === 403) presence.value = { served: false } as PhonePresence
  }
}

function onVisibility() {
  if (document.visibilityState === 'visible') void poll()
}

onMounted(() => {
  void poll().then(schedule)
  document.addEventListener('visibilitychange', onVisibility)
})
onUnmounted(() => {
  stopped = true
  if (timer) { clearTimeout(timer); timer = null }
  document.removeEventListener('visibilitychange', onVisibility)
})
</script>

<template>
  <!-- @click.stop: the status bar toggles the integrity dropdown on click; the icon is a passive indicator. -->
  <span
    v-if="served"
    class="phone-pill"
    :class="'phone-' + state"
    :data-state="state"
    data-testid="phone-pill"
    role="img"
    :aria-label="title"
    :title="title"
    @click.stop
  >
    <!-- 📴 phone off: body + slash -->
    <svg v-if="state === 'unreachable'" class="phone-svg" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <path d="M12 18h.01" />
      <path d="M3 21 21 3" />
    </svg>
    <!-- 📲 phone with an arrow pointing into it: the arrow is the red "attention" part -->
    <svg v-else-if="state === 'needs_login'" class="phone-svg" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="10" y="2" width="10" height="20" rx="2" />
      <path d="M15 18h.01" />
      <g class="phone-arrow">
        <path d="M1.5 12h6" />
        <path d="m5 9 2.5 3L5 15" />
      </g>
    </svg>
    <!-- 📳 vibration mode: body + arcs either side, pulsing -->
    <svg v-else-if="state === 'busy'" class="phone-svg" viewBox="0 0 24 24" aria-hidden="true">
      <g class="phone-body">
        <rect x="7" y="2" width="10" height="20" rx="2" />
        <path d="M12 18h.01" />
      </g>
      <path class="phone-wave phone-wave-l" d="M4 8c-1.6 2.6-1.6 5.4 0 8" />
      <path class="phone-wave phone-wave-r" d="M20 8c1.6 2.6 1.6 5.4 0 8" />
    </svg>
    <!-- 📱 mobile phone -->
    <svg v-else class="phone-svg" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <path d="M12 18h.01" />
    </svg>
  </span>
</template>

<style scoped>
.phone-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  cursor: default;
}
.phone-svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  overflow: visible;
}
/* Colours: red = the status bar's error red, blue = the app's info blue. */
.phone-unreachable { color: #f87171; }
.phone-needs_login,
.phone-ready,
.phone-busy { color: #60a5fa; }
.phone-arrow { stroke: #f87171; stroke-width: 2.4; }

/* 📳: the arcs pulse and the body shivers, suggesting server ↔ phone traffic. */
.phone-wave { animation: phone-wave 0.9s ease-in-out infinite; }
.phone-wave-r { animation-delay: 0.45s; }
.phone-body { animation: phone-shiver 0.3s linear infinite; }
@keyframes phone-wave {
  0%, 100% { opacity: 0.25; }
  50% { opacity: 1; }
}
@keyframes phone-shiver {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-0.6px); }
  75% { transform: translateX(0.6px); }
}
@media (prefers-reduced-motion: reduce) {
  .phone-wave, .phone-body { animation: none; }
}
</style>
